import { KeyActions } from '@solinkcorp/solink-components';
import { SidebarProvider } from '@solinkcorp/solink-ui/components/ui/sidebar';
import { Toaster } from '@solinkcorp/solink-ui/components/ui/sonner';
import { cn } from '@solinkcorp/solink-ui/lib/utils';
import React, { useEffect, useRef, useState } from 'react';
import { useLocalController } from 'solox';

import { useBasicSitesById } from '@solinkcorp/callhome-data';
import { getLocalTimeZone } from '@solinkcorp/utils';
import { useQueryParams } from '@solinkcorp/utils/hooks';

import Monitor from './components/Monitor/Monitor';
import ServiceDisplayDebugger from './components/ServiceDisplayDebugger/ServiceDisplayDebugger';
import {
  DAY_PART_SIDEBAR_WIDTH,
  DAY_PART_SIDEBAR_WIDTH_WEEK,
  DAY_PART_WEEK_INDEX,
  DEBUGGER_PANEL_INITIAL_WIDTH_PX,
} from './constants';
import { ServiceDisplayDebuggerOpenContext } from './context/ServiceDisplayDebuggerContext';
import { ServiceDisplayInjectionProvider } from './context/ServiceDisplayInjectionContext';
import { Controller } from './Controller';
import useServiceDisplayDebugger from './hooks/useServiceDisplayDebugger';
import { ServiceDisplayEventsController } from './ServiceDisplayEventsController';

import { useUserHasFeature } from '@solinkcorp/authentication';
import { useDarkModeSync } from './components/DarkMode/useDarkMode';
import styles from './sdm.module.css';

interface SDMPageProps {
  readOnly?: boolean;
  editMode?: boolean;
  isStandalone?: boolean;
}

const SDMPage: React.FC<SDMPageProps> = ({ editMode, readOnly, isStandalone = false }) => {
  const sitesById = useBasicSitesById();
  const [query] = useQueryParams();
  const selectedSiteId = query.get('siteId') || '';
  const selectedSite = sitesById[selectedSiteId];
  const timezoneId = selectedSite?.timezone ?? getLocalTimeZone();
  const canEdit = useUserHasFeature('sdmWriteAccess') && !readOnly;

  useDarkModeSync();

  const eventsController = useLocalController(() => new ServiceDisplayEventsController());
  const sdmController = useLocalController(() => new Controller());

  const [dayPartIndex, setDayPartIndex] = useState(0);
  const [sidebarWidth, setSidebarWidth] = useState(DAY_PART_SIDEBAR_WIDTH);
  const prevIsWeekView = useRef(false);

  // Persists width unless toggling between day/week views
  const isWeekView = dayPartIndex === DAY_PART_WEEK_INDEX;
  useEffect(() => {
    if (isWeekView !== prevIsWeekView.current) {
      setSidebarWidth(isWeekView ? DAY_PART_SIDEBAR_WIDTH_WEEK : DAY_PART_SIDEBAR_WIDTH);
      prevIsWeekView.current = isWeekView;
    }
  }, [isWeekView]);

  // Debugger functionality
  const { isDebuggerEnabled, isMinimized, toggleDebugger, debuggerActions } =
    useServiceDisplayDebugger();
  const [debuggerWidthPx, setDebuggerWidthPx] = useState(DEBUGGER_PANEL_INITIAL_WIDTH_PX);

  const getMonitorClassName = () => {
    if (!isDebuggerEnabled) return styles.debuggerDisabled;
    if (isMinimized) return styles.debuggerCollapsed;
    return styles.debuggerExpanded;
  };

  const monitorClassName = cn(
    styles.monitorContainer,
    getMonitorClassName(),
    'bg-[#f5f5f5] dark:bg-card',
  );

  const monitorStyle =
    isDebuggerEnabled && !isMinimized
      ? { flex: `0 0 calc(100vw - ${debuggerWidthPx}px)` }
      : undefined;

  return (
    <KeyActions actions={debuggerActions}>
      <ServiceDisplayDebuggerOpenContext.Provider value={isDebuggerEnabled}>
        <ServiceDisplayInjectionProvider>
          <div className={styles.pageContainer}>
            <div className={monitorClassName} style={monitorStyle}>
              <SidebarProvider
                data-testid="sidebar-provider"
                style={{ '--sidebar-width': `${sidebarWidth}px` } as React.CSSProperties}
              >
                <Monitor
                  timezoneId={timezoneId}
                  controller={sdmController}
                  eventsController={eventsController}
                  editMode={editMode}
                  canEdit={canEdit}
                  dayPartIndex={dayPartIndex}
                  onDayPartIndexChange={setDayPartIndex}
                  sidebarWidth={sidebarWidth}
                  onSidebarWidthChange={setSidebarWidth}
                  isStandalone={isStandalone}
                />
              </SidebarProvider>
            </div>

            {/* Service Display Debugger */}
            {isDebuggerEnabled && (
              <ServiceDisplayDebugger
                isEnabled={isDebuggerEnabled}
                isMinimized={isMinimized}
                onToggle={toggleDebugger}
                eventsController={eventsController}
                sdmController={sdmController}
                widthPx={debuggerWidthPx}
                onWidthChange={setDebuggerWidthPx}
              />
            )}
          </div>
          <Toaster position="bottom-right" />
        </ServiceDisplayInjectionProvider>
      </ServiceDisplayDebuggerOpenContext.Provider>
    </KeyActions>
  );
};

export default React.memo(SDMPage);
